/**
 * Rotates the announcement messages.
 *
 * Script rather than CSS because the cycle length depends on how many messages
 * the merchant added, and keyframe percentages cannot be derived from a custom
 * property. The crossfade itself is CSS.
 *
 * interactions.md §3.1: rotate every 5s, pause on hover, and under reduced
 * motion stop entirely showing only the first message.
 */
const INTERVAL = 5000;

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.messages = Array.from(this.querySelectorAll('[data-message]'));

    /* One message is not a rotation, and reduced motion means the first message
       and nothing else — not a faster or instant cycle. */
    if (this.messages.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.index = 0;
    this.pause = this.pause.bind(this);
    this.resume = this.resume.bind(this);

    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);

    this.resume();
  }

  disconnectedCallback() {
    this.pause();
  }

  pause() {
    clearInterval(this.timer);
  }

  resume() {
    this.pause();
    this.timer = setInterval(() => {
      this.show((this.index + 1) % this.messages.length);
    }, INTERVAL);
  }

  /* The hidden messages leave the accessibility tree, so a screen reader is
     given one message rather than all of them at once. */
  show(next) {
    this.messages[this.index].removeAttribute('data-current');
    this.messages[this.index].setAttribute('aria-hidden', 'true');

    this.index = next;

    this.messages[next].setAttribute('data-current', '');
    this.messages[next].removeAttribute('aria-hidden');
  }
}

customElements.define('announcement-bar', AnnouncementBar);
